import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";

import { useGetSalesQuery } from "../../slices/salesApiSlice";
import { useGetExpensesQuery } from "../../slices/expensesApiSlice";
import { useGetIncomesQuery } from "../../slices/incomesApiSlice";
import { useGetDepositsQuery } from "../../slices/depositsApiSlice";
import { useGetSuppliesQuery } from "../../slices/suppliesApiSlice";
import { useGetOtherExpensesQuery } from "../../slices/otherExpensesApiSlice";

export default function AllTotal() {
  const today = new Date().toISOString().split("T")[0];

  // Fetch all data
  const { data: salesData } = useGetSalesQuery();
  const { data: expensesData } = useGetExpensesQuery({ startDate: "1900-01-01", endDate: today });
  const { data: incomesData } = useGetIncomesQuery({ startDate: "1900-01-01", endDate: today });
  const { data: depositsData } = useGetDepositsQuery({ startDate: "1900-01-01", endDate: today });
  const { data: suppliesData } = useGetSuppliesQuery();
  const { data: otherExpensesData } = useGetOtherExpensesQuery({ startDate: "1900-01-01", endDate: today });

  // Opening date (fixed)
  const openingIncome = incomesData?.incomes?.find((i) => i.category === "Opening");
  const openingDeposit = depositsData?.deposits?.find((d) => d.category === "Opening");

  const openingDate = openingIncome?.date
    ? new Date(openingIncome.date).toISOString().split("T")[0]
    : today;

  // User-selected end date
  const [endDate, setEndDate] = useState(today);

  const [totals, setTotals] = useState({
    openingCash: 0,
    openingBank: 0,
    totalIncomes: 0,
    totalDeposits: 0,
    totalPurchases: 0,
    totalOtherExpenses: 0,
    cashInHand: 0,
  });

  useEffect(() => {
    if (!incomesData || !depositsData || !suppliesData || !otherExpensesData) return;

    // Opening values
    const openingCash = Number(openingIncome?.amount || 0);
    const openingBank = Number(openingDeposit?.amount || 0);

    // Filter by openingDate → endDate
    const filterRange = (item) => {
      if (!item.date) return false;
      const d = new Date(item.date).toISOString().split("T")[0];
      return d >= openingDate && d <= endDate;
    };

    const totalIncomes = incomesData.incomes
      .filter(filterRange)
      .reduce((sum, i) => sum + Number(i.amount || 0), 0);

    const totalDepositsRaw = depositsData.deposits
      .filter(filterRange)
      .reduce((sum, d) => sum + Number(d.amount || 0), 0);

    //const totalDeposits = totalDepositsRaw > 0 ? totalDepositsRaw * -1 : totalDepositsRaw;
     const totalDeposits = totalDepositsRaw
    const totalPurchasesRaw = suppliesData.supplies
      .filter(filterRange)
      .reduce((sum, s) => sum + Number(s.cardCost || 0), 0);

    const totalPurchases = totalPurchasesRaw * -1;

    const totalOtherExpenses = otherExpensesData.otherExpenses
      .filter(filterRange)
      .reduce((sum, o) => sum + Number(o.amount || 0), 0);

    // New formula
    const cashInHand =
      (openingCash +
      openingBank +
      (totalIncomes - openingCash) +
      totalOtherExpenses) -
      totalDeposits +
      totalPurchases;

    setTotals({
      openingCash: openingCash.toFixed(2),
      openingBank: openingBank.toFixed(2),
      totalIncomes: totalIncomes.toFixed(2),
      totalDeposits: totalDeposits.toFixed(2),
      totalPurchases: totalPurchases.toFixed(2),
      totalOtherExpenses: totalOtherExpenses.toFixed(2),
      cashInHand: cashInHand.toFixed(2),
    });
  }, [incomesData, depositsData, suppliesData, otherExpensesData, openingDate, endDate]);

  // Export to Excel
  const exportToExcel = () => {
    const exportData = [
      { Header: "Opening Cash", Value: totals.openingCash },
      { Header: "Opening Bank Balance", Value: totals.openingBank },
      { Header: "Total Incomes", Value: totals.totalIncomes },
      { Header: "Total Deposits", Value: totals.totalDeposits },
      { Header: "Total Purchases", Value: totals.totalPurchases },
      { Header: "Total Other Expenses", Value: totals.totalOtherExpenses },
      { Header: "Cash In Hand", Value: totals.cashInHand },
    ];

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "CashFlowSummary");
    XLSX.writeFile(workbook, "CashFlowSummary.xlsx");
  };

  return (
    <div className="all-total">
      <h1>Cash Flow Summary</h1>

      <div className="date-boxes">
        <div>
          <label>Opening Date (Fixed):</label>
          <input type="date" value={openingDate} readOnly />
        </div>

        <div>
          <label>Calculate Up To:</label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      <table className="totals-table">
        <thead>
          <tr>
            <th>Header</th>
            <th>Total</th>
          </tr>
        </thead>

        <tbody>
          <tr>
            <td><strong>Opening Cash</strong></td>
            <td>${totals.openingCash}</td>
          </tr>

          <tr>
            <td><strong>Opening Bank Balance</strong></td>
            <td>${totals.openingBank}</td>
          </tr>

          <tr>
            <td><strong>Total Incomes</strong></td>
            <td>${totals.totalIncomes-totals.openingCash}</td>
          </tr>

          <tr>
            <td><strong>Total Purchases</strong></td>
            <td>${totals.totalPurchases}</td>
          </tr>

          <tr>
            <td><strong>Total Other Expenses</strong></td>
            <td>${totals.totalOtherExpenses}</td>
          </tr>

          <tr>
            <td><strong>Cash At Bank</strong></td>
            <td>${totals.totalDeposits}</td>
          </tr>

          <tr>
            <td><strong>Cash In Hand</strong></td>
            <td>${totals.cashInHand}</td>
          </tr>
        </tbody>
      </table>

      <button onClick={exportToExcel} className="export-button">
        Export to Excel
      </button>
    </div>
  );
}
